// @dynamic-i18n-keys
// Dynamic t() at line 280 (translationKey):
//   editHistory.item
//   editHistory.item.original
import { compose } from 'redux';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiUser, PapiComment } from '@wix/da-papi-types';
import { sanitizeHref, sanitizeUrl, Url } from '@wix/da-url';
import {
  WithMobileContextProps,
  withMobileContext,
} from '@wix/da-react-context/pkg/MobileContext';
import Flex from '@wix/da-ds/pkg/Flex';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import StandardDropdown from '@wix/da-ds/pkg/Dropdown/Preset/StandardDropdown';
import CommentThread from '@wix/da-shared-react/pkg/CommentThread';
import Comment from '@wix/da-shared-react/pkg/Comment/presets/Comment';
import { CommentPublisherResult } from '@wix/da-shared-react/pkg/CommentEditor/redux/types';
import { CommentableItem } from '../../../types/comments';
import Commentable from '../Commentable';
import React from 'react';

import s from './CommentPage.scss';

export interface Props {
  itemId: number;
  typeId: number;
  commentId: number;
  streamId?: string;
  item?: CommentableItem;
  editId?: number;
  parent?: PapiComment;
  canPostComment?: boolean;
  parentHistory?: {
    editId?: number;
    edited: string;
  }[];
  top?: PapiComment;
  currentUser?: PapiUser;
  isCanonical?: boolean;
  isInfinite?: boolean;
  onThreadUpdated?: (streamId: string, result: CommentPublisherResult) => void;
}

interface EditHistoryItem {
  editId?: number;
  label: string;
  url: string;
}

export class CommentPage extends React.Component<
  Props & WithTranslation & WithMobileContextProps
> {
  parentCommentRef = React.createRef<HTMLDivElement>();

  render() {
    return this.props.isMobile ? this.renderMobile() : this.renderDesktop();
  }

  renderMobile() {
    const {
      typeId,
      itemId,
      parent,
      parentHistory,
      isInfinite,
      isCanonical,
      top,
      item,
    } = this.props;

    const withEditHistory =
      isCanonical && parent && parentHistory && parentHistory.length > 0;

    if (!typeId || !itemId) {
      return null;
    }

    return (
      <div className={s['root']}>
        {isCanonical && (
          <div>
            <div
              className={classnames(
                s['context'],
                withEditHistory && s['context-with-history']
              )}
            >
              {((parent && !!parent.parentId) || top) && this.renderAncestry()}
              {item && <Commentable item={item} parent={parent} />}
              {withEditHistory && this.renderMobileEditHistory()}
            </div>
          </div>
        )}
        <Flex>
          <div
            className={classnames(s['comments'], isCanonical && s['canonical'])}
          >
            {parent && this.renderContextComment()}
            <CommentThread
              className={s['comment-thread']}
              statsAndControls={isCanonical ? 'off' : 'on'}
              commentableTypeId={typeId}
              commentableItemId={itemId}
              parentComment={parent}
              parentCommentRef={this.parentCommentRef}
              postbox={'none'}
              isInfinite={isInfinite}
            />
          </div>
        </Flex>
      </div>
    );
  }

  renderDesktop() {
    const {
      typeId,
      itemId,
      parent,
      parentHistory,
      isInfinite,
      isCanonical,
      top,
      item,
      t,
    } = this.props;

    const withEditHistory =
      isCanonical && parent && parentHistory && parentHistory.length > 0;

    if (!typeId || !itemId) {
      return null;
    }

    return (
      <div className={s['root']}>
        {isCanonical && (
          <div className={s['header']}>
            <div className={s['context']}>
              {((parent && !!parent.parentId) || top) && this.renderAncestry()}
              {item && <Commentable item={item} parent={parent} />}
            </div>
            {withEditHistory && (
              <div className={s['edit-history-title']}>
                <span>{t('editHistory.title')}</span>
              </div>
            )}
          </div>
        )}
        <Flex justifyContent="center">
          <div
            className={classnames(s['comments'], isCanonical && s['canonical'])}
          >
            {parent && this.renderContextComment()}
            <CommentThread
              className={s['comment-thread']}
              statsAndControls={isCanonical ? 'off' : 'on'}
              commentableTypeId={typeId}
              commentableItemId={itemId}
              parentComment={parent}
              parentCommentRef={this.parentCommentRef}
              postbox={'none'}
              isInfinite={isInfinite}
            />
          </div>
          {withEditHistory && (
            <div className={s['edit-history']}>{this.renderEditHistory()}</div>
          )}
        </Flex>
      </div>
    );
  }

  renderAncestry() {
    const { parent, top, t } = this.props;
    return (
      <div className={s['ancestry']}>
        {parent && !!parent.parentId && (
          <a
            className={s['ancestor-link']}
            href={Url.commentLink(
              parent.typeId,
              parent.itemId,
              parent.parentId
            )}
          >
            {t('comments:contextHeader.parent')}
          </a>
        )}
        {top && (
          <a
            className={s['ancestor-link']}
            href={Url.commentLink(top.typeId, top.itemId, top.commentId)}
          >
            {t('contextHeader.top')}
          </a>
        )}
      </div>
    );
  }

  onThreadUpdated = (result: CommentPublisherResult) => {
    const { onThreadUpdated, streamId } = this.props;
    streamId && onThreadUpdated && onThreadUpdated(streamId, result);
  };

  renderContextComment() {
    const { parent, canPostComment } = this.props;
    if (!parent) {
      return null;
    }
    return (
      <div ref={this.parentCommentRef}>
        <Comment
          className={s['context-comment']}
          comment={parent}
          onReplyPosted={this.onThreadUpdated}
          canPostComment={!!canPostComment}
        />
      </div>
    );
  }

  renderEditHistory() {
    const { editId } = this.props;
    return (
      <div>
        {this.getEditHistory().map(item => (
          <div
            key={`${item.label}-${item.editId}`}
            className={classnames(
              s['edit-history-item'],
              editId === item.editId && s['active']
            )}
          >
            <a href={sanitizeHref(item.url)}>{item.label}</a>
          </div>
        ))}
      </div>
    );
  }

  renderMobileEditHistory() {
    const { editId } = this.props;
    return (
      <StandardDropdown
        role="menu"
        selectedValue={editId || -1}
        items={this.getEditHistory().map(item => ({
          link: sanitizeUrl(item.url),
          value: item.editId || -1,
          label: item.label,
        }))}
        className={s['edit-history-dropdown']}
        toggleClassName={s['edit-history-dropdown-toggle']}
        itemClassName={s['edit-history-dropdown-item']}
        arrowClassName={s['edit-history-dropdown-arrow']}
      />
    );
  }

  getEditHistory(): EditHistoryItem[] {
    const { parent, parentHistory, t } = this.props;
    if (!parent || !parentHistory) {
      return [];
    }
    const result: EditHistoryItem[] = parentHistory.map(
      (commentRevision, i) => {
        let translationKey = 'editHistory.item';

        if (i === 0) {
          translationKey = 'editHistory.item.original';
        }

        const timestamp = formatDate(
          commentRevision.edited,
          DATE_FORMATS.DateTime.Short,
          this.props.i18n.language
        );

        return {
          editId: commentRevision.editId,
          label: t(translationKey, { timestamp }),
          url: Url.commentLink(
            parent.typeId,
            parent.itemId,
            parent.commentId,
            commentRevision.editId
          ),
        };
      }
    );
    result.push({
      label: t('editHistory.item.latest'),
      url: Url.commentLink(parent.typeId, parent.itemId, parent.commentId),
      editId: undefined,
    });
    result.reverse();
    return result;
  }
}

const enhance = compose(withMobileContext, withTranslation());

export default enhance(CommentPage) as React.ComponentType<Props>;
